import React, { useState } from 'react';
import styled from 'styled-components';

const VendorsContainer = styled.div`
  background-color: #0a0a0a;
  border-radius: 8px;
  padding: 20px;
  color: white;
  max-height: 500px;
  overflow-y: auto;
`;

const VendorsHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const VendorsTitle = styled.h3`
  color: #D4AF37;
  margin: 0 0 5px 0;
  font-size: 24px;
`;

const VendorsSubtitle = styled.p`
  color: #ccc;
  margin: 0;
  font-size: 14px;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 5px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
  &::-webkit-scrollbar-thumb {
    background: #D4AF37;
    border-radius: 2px;
  }
`;

const CategoryTab = styled.button`
  padding: 8px 16px;
  background-color: ${props => props.active ? '#D4AF37' : '#1a1a1a'};
  color: ${props => props.active ? 'black' : '#D4AF37'};
  border: 1px solid #D4AF37;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  white-space: nowrap;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  
  &:hover {
    background-color: ${props => props.active ? '#B8860B' : '#2a2a2a'};
  }
`;

const VendorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const VendorCard = styled.div`
  background-color: #1a1a1a;
  border: 2px solid #333;
  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: #D4AF37;
    background-color: #222;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(212, 175, 55, 0.2);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const VendorBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #D4AF37;
  color: black;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
`;

const VendorName = styled.h4`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: bold;
`;

const VendorDescription = styled.p`
  color: #ccc;
  margin: 0 0 10px 0;
  font-size: 14px;
  line-height: 1.4;
`;

const VendorSpecialties = styled.div`
  margin-bottom: 10px;
`;

const VendorSpecialtiesTitle = styled.p`
  color: #D4AF37;
  margin: 0 0 5px 0;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
`;

const VendorSpecialtiesList = styled.p`
  color: #fff;
  margin: 0;
  font-size: 13px;
  font-weight: 500;
`;

const VendorLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #888;
  font-size: 12px;
  margin-top: 10px;
`;

const LocationIcon = styled.span`
  color: #D4AF37;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #333;
  justify-content: center;
`;

const ActionButton = styled.button`
  background-color: #D4AF37;
  border: none;
  color: black;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  
  &:hover {
    background-color: #B8860B;
  }
`;

const CancelButton = styled(ActionButton)`
  background-color: #666;
  color: white;
  
  &:hover {
    background-color: #777;
  }
`;

const FeaturedVendors = ({ onSelectVendor, onCancel }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Featured vendors data
  const vendorsData = {
    food: [
      {
        id: 'bbq-smokehouse',
        name: "BBQ Smoke House",
        description: "Authentic Texas-style BBQ with slow-smoked meats and traditional sides",
        specialties: ["Brisket", "Pulled Pork", "Ribs", "Mac & Cheese"],
        location: "Food Court - Section A",
        badge: "Popular",
        category: 'food'
      },
      {
        id: 'gourmet-burgers',
        name: "Gourmet Burger Station",
        description: "Artisan burgers made with premium ingredients and creative toppings",
        specialties: ["Truffle Burger", "Veggie Deluxe", "BBQ Bacon Burger"],
        location: "Main Street - Booth 12",
        badge: "New",
        category: 'food'
      },
      {
        id: 'taco-fiesta',
        name: "Taco Fiesta",
        description: "Authentic Mexican street food with fresh ingredients and bold flavors",
        specialties: ["Fish Tacos", "Carnitas", "Vegetarian Tacos", "Elote"],
        location: "Festival Plaza - Corner Spot",
        badge: "Featured",
        category: 'food'
      },
      {
        id: 'pizza-wood-fire',
        name: "Pizza Wood Fire",
        description: "Neapolitan-style wood-fired pizzas with fresh mozzarella and house sauce",
        specialties: ["Margherita", "Pepperoni", "Prosciutto & Arugula"],
        location: "Food Court - Section B",
        badge: "Hot",
        category: 'food'
      },
      {
        id: 'asian-fusion',
        name: "Asian Fusion Express",
        description: "Modern Asian cuisine with fusion twists and traditional favorites",
        specialties: ["Pad Thai", "Korean BBQ Bowl", "Sushi Rolls"],
        location: "International Row - Booth 8",
        badge: "Trending",
        category: 'food'
      }
    ],
    drinks: [
      {
        id: 'craft-cocktails',
        name: "Craft Cocktail Corner",
        description: "Premium cocktails made with top-shelf spirits and fresh ingredients",
        specialties: ["Old Fashioned", "Craft Margarita", "Seasonal Specials"],
        location: "Bar District - Central",
        badge: "Premium",
        category: 'drinks'
      },
      {
        id: 'local-brewery',
        name: "Local Brew Station",
        description: "Featuring the best local craft beers and seasonal brews",
        specialties: ["IPA", "Wheat Beer", "Seasonal Ale", "Root Beer Float"],
        location: "Beer Garden - East Side",
        badge: "Local",
        category: 'drinks'
      },
      {
        id: 'fresh-juice',
        name: "Fresh Juice Bar",
        description: "Cold-pressed juices, smoothies, and healthy beverage options",
        specialties: ["Green Juice", "Tropical Smoothie", "Kombucha"],
        location: "Health Corner - Booth 3",
        badge: "Healthy",
        category: 'drinks'
      }
    ],
    sweets: [
      {
        id: 'artisan-ice-cream',
        name: "Artisan Ice Cream Co.",
        description: "Small-batch ice cream with unique flavors and premium ingredients",
        specialties: ["Lavender Honey", "Salted Caramel", "Chocolate Bourbon"],
        location: "Sweet Street - Booth 5",
        badge: "Artisan",
        category: 'sweets'
      },
      {
        id: 'donut-shop',
        name: "Gourmet Donut Shop",
        description: "Hand-crafted donuts with creative toppings and seasonal flavors",
        specialties: ["Maple Bacon", "Glazed Old Fashioned", "Seasonal Fruit"],
        location: "Sweet Street - Booth 7",
        badge: "Fresh",
        category: 'sweets'
      },
      {
        id: 'cotton-candy',
        name: "Cotton Candy Dreams",
        description: "Fresh spun cotton candy in amazing flavors and colors",
        specialties: ["Blue Raspberry", "Pink Vanilla", "Rainbow Swirl"],
        location: "Family Fun Zone",
        badge: "Kids Love",
        category: 'sweets'
      }
    ],
    snacks: [
      {
        id: 'kettle-corn',
        name: "Kettle Corn Kingdom",
        description: "Fresh popped kettle corn and gourmet popcorn varieties",
        specialties: ["Classic Kettle", "Caramel Corn", "Spicy Cheese"],
        location: "Snack Alley - Booth 2",
        badge: "Classic",
        category: 'snacks'
      },
      {
        id: 'pretzel-paradise',
        name: "Pretzel Paradise",
        description: "Warm soft pretzels with various dips and toppings",
        specialties: ["Salted Pretzel", "Cinnamon Sugar", "Beer Cheese Dip"],
        location: "Snack Alley - Booth 4",
        badge: "Warm",
        category: 'snacks'
      }
    ]
  };

  const categories = [
    { id: 'all', name: 'All Vendors', emoji: '🎪' },
    { id: 'food', name: 'Food Trucks', emoji: '🍔' },
    { id: 'drinks', name: 'Beverages', emoji: '🍹' },
    { id: 'sweets', name: 'Desserts', emoji: '🍦' },
    { id: 'snacks', name: 'Snacks', emoji: '🍿' }
  ];

  const getFilteredVendors = () => {
    if (selectedCategory === 'all') {
      return [...vendorsData.food, ...vendorsData.drinks, ...vendorsData.sweets, ...vendorsData.snacks];
    }
    return vendorsData[selectedCategory] || [];
  };

  const handleVendorClick = (vendor) => {
    onSelectVendor({
      ...vendor,
      action: 'show_vendor_menu'
    });
  };

  return (
    <VendorsContainer>
      <VendorsHeader>
        <VendorsTitle>🎪 Featured Food Trucks & Vendors</VendorsTitle>
        <VendorsSubtitle>Discover the amazing vendors at our festival!</VendorsSubtitle>
      </VendorsHeader>

      <CategoryTabs>
        {categories.map(category => (
          <CategoryTab
            key={category.id}
            active={selectedCategory === category.id}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.emoji} {category.name}
          </CategoryTab>
        ))}
      </CategoryTabs>

      <VendorsGrid>
        {getFilteredVendors().map(vendor => (
          <VendorCard key={vendor.id} onClick={() => handleVendorClick(vendor)}>
            <VendorBadge>{vendor.badge}</VendorBadge>
            
            <VendorName>{vendor.name}</VendorName>
            
            <VendorDescription>{vendor.description}</VendorDescription>
            
            <VendorSpecialties>
              <VendorSpecialtiesTitle>Specialties:</VendorSpecialtiesTitle>
              <VendorSpecialtiesList>{vendor.specialties.join(' • ')}</VendorSpecialtiesList>
            </VendorSpecialties>
            
            <VendorLocation>
              <LocationIcon>📍</LocationIcon>
              {vendor.location}
            </VendorLocation>
          </VendorCard>
        ))}
      </VendorsGrid>

      <ActionButtons>
        <CancelButton onClick={onCancel}>
          Close
        </CancelButton>
        <ActionButton onClick={() => onSelectVendor({ action: 'show_full_menu' })}>
          View Full Festival Menu
        </ActionButton>
      </ActionButtons>
    </VendorsContainer>
  );
};

export default FeaturedVendors;